// src/api/api.ts
import { ConversationRequest } from "./models";
import { CHAT_API_URL } from "./config";

let authToken: string | null = null;
let activeConfigLanguage: "en" | "ga" = "en";
let latestSessionRequestId = 0;

// Cached app config from the token response
export interface AppConfig {
  suggested_questions?: string[];
  stt_silence_timeout_ms?: number;
}
let appConfig: AppConfig = {};

// Storage keys (sessionStorage for single-tab isolation)
const AUTH_TOKEN_KEY = 'auth-token';

export interface RateLimitInfo {
  retryAfter: number; // seconds
  remaining: number;
  limit: number;
  resetAt: Date;
}

export class RateLimitError extends Error {
  constructor(
    public rateLimitInfo: RateLimitInfo,
    message?: string
  ) {
    super(message || `Rate limit exceeded. Retry after ${rateLimitInfo.retryAfter} seconds.`);
    this.name = 'RateLimitError';
  }
}

function getStoredToken(): string | null {
  try {
    return sessionStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

function setStoredToken(token: string | null): void {
  try {
    if (token) {
      sessionStorage.setItem(AUTH_TOKEN_KEY, token);
    } else {
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
    }
  } catch (e) {
    console.warn('Failed to store auth token:', e);
  }
}


function parseRateLimitHeaders(headers: Headers): RateLimitInfo | null {
  const retryAfter = headers.get('Retry-After');
  const remainingMinute = headers.get('X-RateLimit-Remaining-Minute');
  // const remainingHour = headers.get('X-RateLimit-Remaining-Hour');
  const resetMinute = headers.get('X-RateLimit-Reset-Minute');

  if (!retryAfter) return null;

  return {
    retryAfter: parseInt(retryAfter, 10),
    remaining: remainingMinute ? parseInt(remainingMinute, 10) : 0,
    limit: 5, // From config
    resetAt: resetMinute ? new Date(parseInt(resetMinute, 10) * 1000) : new Date(Date.now() + parseInt(retryAfter, 10) * 1000),
  };
}

function normalizeLanguageCode(languageCode?: string | null): "en" | "ga" {
  if (!languageCode) {
    return "en";
  }

  const normalized = languageCode.trim().toLowerCase();
  if (normalized.startsWith("ga")) {
    return "ga";
  }

  return "en";
}

function detectUiLanguage(): "en" | "ga" {
  if (typeof document !== "undefined") {
    const htmlLang = document.documentElement?.lang;
    if (htmlLang) {
      return normalizeLanguageCode(htmlLang);
    }
  }

  if (typeof window !== "undefined" && window.location.pathname.includes('/ga/')) {
    return "ga";
  }

  return "en";
}

async function fetchToken(languageCode?: string): Promise<{ token: string; config?: AppConfig }> {
  const resolvedLanguage = normalizeLanguageCode(languageCode ?? activeConfigLanguage ?? detectUiLanguage());

  const url = new URL(`${CHAT_API_URL}/api/token`);
  url.searchParams.set("lang", resolvedLanguage);

  const resp = await fetch(url.toString(), { method: "GET" });
  if (resp.status === 429) {
    // Token endpoint is also rate limited (IP-based)
    const rateLimitInfo = parseRateLimitHeaders(resp.headers);
    if (rateLimitInfo) {
      throw new RateLimitError(rateLimitInfo, "Too many session requests. Please wait.");
    }
    throw new Error('Rate limit exceeded on token endpoint');
  }
  if (!resp.ok) throw new Error("Unable to fetch auth token");
  return resp.json();
}

/**
 * Eagerly fetch the auth token and app config on app startup.
 * Call this once when the app mounts so that appConfig (suggested questions)
 * is available before the user interacts.
 * Always fetches fresh config from the backend.
 */
export async function initializeSession(languageCode?: string, forceRefresh = false): Promise<AppConfig> {
  const resolvedLanguage = normalizeLanguageCode(languageCode ?? detectUiLanguage());
  const languageChanged = resolvedLanguage !== activeConfigLanguage;
  activeConfigLanguage = resolvedLanguage;
  const requestId = ++latestSessionRequestId;

  if (forceRefresh || languageChanged) {
    authToken = null;
    appConfig = {};
    setStoredToken(null);
  }

  // If we already have config in memory (e.g. SPA navigation), return it
  if (!forceRefresh && !languageChanged && authToken && appConfig.suggested_questions) {
    return appConfig;
  }

  // Fetch fresh token + config from backend
  try {
    const result = await fetchToken(resolvedLanguage);
    if (requestId !== latestSessionRequestId || resolvedLanguage !== activeConfigLanguage) {
      return appConfig;
    }
    authToken = result.token;
    setStoredToken(result.token);
    if (result.config) {
      appConfig = result.config;
    }
  } catch (e) {
    if (requestId !== latestSessionRequestId || resolvedLanguage !== activeConfigLanguage) {
      return appConfig;
    }
    console.warn('Failed to initialize session:', e);
    // Fall back to stored token if fresh fetch fails
    if (!authToken) {
      authToken = getStoredToken();
    }
  }

  return appConfig;
}

async function apiFetch(
  url: string,
  options: RequestInit = {},
  retry = true
): Promise<Response> {
  // Try to use stored token first, then in-memory, then fetch new
  if (!authToken) {
    authToken = getStoredToken();
  }
  if (!authToken) {
    const result = await fetchToken(activeConfigLanguage);
    authToken = result.token;
    if (result.config) {
      appConfig = result.config;
    }
    setStoredToken(result.token);
  }
  
  const headers = new Headers(options.headers);
  headers.set("Authorization", `Bearer ${authToken}`);
  // Note: Rate limits are now embedded in the JWT token - no separate header needed
  
  options.headers = headers;

  const resp = await fetch(url, options);
  
  // Update auth token from response (contains updated rate limits)
  const updatedToken = resp.headers.get('X-Updated-Token');
  if (updatedToken) {
    authToken = updatedToken;
    setStoredToken(updatedToken);
  }
  
  // Handle rate limiting (429)
  if (resp.status === 429) {
    const rateLimitInfo = parseRateLimitHeaders(resp.headers);
    if (rateLimitInfo) {
      throw new RateLimitError(rateLimitInfo);
    }
    throw new Error('Rate limit exceeded');
  }
  
  // Handle authentication errors (token expired/invalid)
  if ((resp.status === 401 || resp.status === 403) && retry) {
    // Clear stored token and fetch new one
    authToken = null;
    setStoredToken(null);
    const result = await fetchToken(activeConfigLanguage);
    authToken = result.token;
    if (result.config) {
      appConfig = result.config;
    }
    setStoredToken(result.token);
    return apiFetch(url, options, false);
  }
  
  return resp;
}

export async function* sendChat(
  payload: ConversationRequest
): AsyncGenerator<any> {
  try {
    const resp = await apiFetch(`${CHAT_API_URL}/api/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({} as any));
      throw new Error(err.error || `Chat failed (${resp.status})`);
    }

    const reader = resp.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          yield JSON.parse(line);
        } catch (e) {
          console.warn("Could not parse line:", line, e);
        }
      }
    }
  } catch (error) {
    // Re-throw RateLimitError as-is for component handling
    if (error instanceof RateLimitError) {
      throw error;
    }
    throw error;
  }
}

export async function sendFeedback(feedback: any) {
  const resp = await apiFetch(`${CHAT_API_URL}/api/message/feedback`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(feedback),
  });
  if (!resp.ok) throw new Error(`Feedback failed (${resp.status})`);
  return resp.json();
}

export async function sendReport(report: any) {
  const resp = await apiFetch(`${CHAT_API_URL}/api/message/report`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(report),
  });
  if (!resp.ok) throw new Error(`Report failed (${resp.status})`);
  return resp.json();
}

export async function synthesizeSpeech(
  text: string,
  messageId?: string,
  voiceName?: string,
  speed?: number,
  speechProvider: 'openai' | 'abair' = 'openai',
  signal?: AbortSignal,
) {
  const resp = await apiFetch(`${CHAT_API_URL}/api/synthesis`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Speech-Provider": speechProvider,
    },
    body: JSON.stringify({ text, messageId, voiceName, speed }),
    signal,
  });
  if (!resp.ok) throw new Error(`Synthesis failed (${resp.status})`);
  return resp.json();
}

export async function getVoiceMetadata(speechProvider: 'openai' | 'abair' = 'openai') {
  const resp = await apiFetch(`${CHAT_API_URL}/api/voices`, {
    method: "GET",
    headers: {
      "X-Speech-Provider": speechProvider,
    },
  });
  if (!resp.ok) throw new Error(`Voice metadata failed (${resp.status})`);
  return resp.json();
}

/**
 * Convert an audio Blob (WebM/Opus, MP4, etc.) to WAV format using Web Audio API.
 * WAV is universally supported by all speech-to-text APIs with no container ambiguity.
 * Downsamples to 16kHz mono for optimal STT performance and smaller file size.
 */
async function convertToWav(blob: Blob): Promise<Blob> {
  const arrayBuffer = await blob.arrayBuffer();
  const audioContext = new AudioContext({ sampleRate: 16000 });

  try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Mix down to mono if stereo
    const numberOfChannels = 1;
    const sampleRate = audioBuffer.sampleRate;
    const samples = audioBuffer.getChannelData(0); // Use first channel

    // If AudioContext resampled, use that; otherwise we have the original rate
    const length = samples.length;

    // Build WAV file
    const wavBuffer = new ArrayBuffer(44 + length * 2);
    const view = new DataView(wavBuffer);

    // WAV header
    const writeString = (offset: number, str: string) => {
      for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true); // fmt chunk size
    view.setUint16(20, 1, true); // PCM format
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numberOfChannels * 2, true); // byte rate
    view.setUint16(32, numberOfChannels * 2, true); // block align
    view.setUint16(34, 16, true); // bits per sample
    writeString(36, 'data');
    view.setUint32(40, length * 2, true);

    // Convert float32 samples to int16
    const offset = 44;
    for (let i = 0; i < length; i++) {
      const s = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(offset + i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }

    return new Blob([wavBuffer], { type: 'audio/wav' });
  } finally {
    await audioContext.close();
  }
}

/**
 * Transcribe audio to text using the speech-to-text API.
 * Converts audio to WAV before sending to ensure compatibility with Azure OpenAI.
 * @param audioBlob - Audio blob from MediaRecorder
 * @returns Transcribed text
 */
export async function transcribeSpeech(audioBlob: Blob): Promise<string> {
  // Convert to WAV for universal compatibility — Chrome's WebM/Opus output
  // is frequently rejected by Azure OpenAI's parser despite being "supported"
  let fileBlob: Blob;
  let filename: string;
  try {
    fileBlob = await convertToWav(audioBlob);
    filename = 'recording.wav';
    console.log('[transcribeSpeech] Converted to WAV:', fileBlob.size, 'bytes');
  } catch (e) {
    // Fallback: send original blob if conversion fails (e.g., unsupported codec)
    console.warn('[transcribeSpeech] WAV conversion failed, sending original:', e);
    const ext = audioBlob.type.includes('mp4') || audioBlob.type.includes('m4a') ? 'm4a' : 'webm';
    fileBlob = audioBlob;
    filename = `recording.${ext}`;
  }

  const formData = new FormData();
  formData.append("file", fileBlob, filename);

  const resp = await apiFetch(`${CHAT_API_URL}/api/speech/transcribe`, {
    method: "POST",
    body: formData,
    // Note: Don't set Content-Type header - browser will set it with boundary
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: "Transcription failed" }));
    throw new Error(err.detail || `Transcription failed (${resp.status})`);
  }

  const result = await resp.json();
  return result.text;
}

/**
 * Get the app config that was received with the auth token.
 * Contains dynamic configuration like suggested questions.
 */
export function getAppConfig(): AppConfig {
  return appConfig;
}
